import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, EnvelopeSimple, MapPin, LinkedinLogo, InstagramLogo, Star, SignIn } from '@phosphor-icons/react';
import axios from 'axios';
import { serviceSlug } from './ServicesBentoGrid';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const socialLinks = [
  { icon: LinkedinLogo, href: 'https://www.linkedin.com/company/north-somerset-environment', label: 'LinkedIn' },
  { icon: InstagramLogo, href: 'https://www.instagram.com/nsecltd', label: 'Instagram' },
  { icon: Star, href: 'https://uk.trustpilot.com/review/nsecltd.co.uk', label: 'Trustpilot' },
];

const footerLinks = {
  company: [
    { name: 'About Us', path: '/about' },
    { name: 'News', path: '/news' },
    { name: 'Community', path: '/community' },
    { name: 'Case Studies', path: '/case-studies' },
    { name: 'Careers', path: '/careers' },
    { name: 'Contact', path: '/contact' },
  ],
};

export const SiteFooter = () => {
  const [footerDocs, setFooterDocs] = useState([]);
  const [services, setServices] = useState([]);
  const [partners, setPartners] = useState([]);

  useEffect(() => {
    axios.get(`${API}/footer-documents`).then(r => {
      if (r.data && r.data.length > 0) setFooterDocs(r.data);
    }).catch(() => {});
    axios.get(`${API}/services`).then(r => {
      if (r.data && r.data.length > 0) setServices(r.data);
    }).catch(() => {});
    axios.get(`${API}/footer-partners`).then(r => {
      if (r.data && r.data.length > 0) setPartners(r.data);
    }).catch(() => {});
  }, []);

  return (
    <footer className="bg-neutral-900 border-t border-neutral-800" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* Contact Info */}
          <div className="lg:col-span-1">
            <h3 className="font-outfit font-semibold text-white mb-6">Contact Us</h3>
            <div className="space-y-3">
              <a href="tel:07821666083" className="flex items-center gap-3 text-neutral-400 hover:text-lime-400 transition-colors duration-200" data-testid="footer-phone-link">
                <Phone size={20} weight="duotone" className="text-lime-500" />
                <span className="text-sm">07821 666 083</span>
              </a>
              <a href="mailto:enquiries@nsecltd.co.uk" className="flex items-center gap-3 text-neutral-400 hover:text-lime-400 transition-colors duration-200" data-testid="footer-email-link">
                <EnvelopeSimple size={20} weight="duotone" className="text-lime-500" />
                <span className="text-sm">enquiries@nsecltd.co.uk</span>
              </a>
              <div className="flex items-start gap-3 text-neutral-400">
                <MapPin size={20} weight="duotone" className="text-lime-500 flex-shrink-0 mt-1" />
                <span className="text-sm">Unit 5-6 Westlands Distribution Park<br />Winterstoke Road<br />Weston-Super-Mare, BS24 9AD</span>
              </div>
            </div>
            {/* Social Icons */}
            <div className="flex items-center gap-3 mt-5">
              {socialLinks.map((s) => (
                <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg bg-neutral-800 text-neutral-400 hover:text-lime-400 hover:bg-neutral-700 transition-all" data-testid={`footer-social-${s.label.toLowerCase()}`} aria-label={s.label}>
                  <s.icon size={18} weight="bold" />
                </a>
              ))}
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className="font-outfit font-semibold text-white mb-6">Our Services</h3>
            <ul className="space-y-3">
              {services.length > 0 ? services.map((s) => (
                <li key={s.id}>
                  <Link to={`/services#${serviceSlug(s.title)}`} className="footer-link text-sm" data-testid={`footer-service-${serviceSlug(s.title)}`}>
                    {s.title}
                  </Link>
                </li>
              )) : (
                <li><span className="text-neutral-600 text-sm">Loading…</span></li>
              )}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="font-outfit font-semibold text-white mb-6">Company</h3>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link to={link.path} className="footer-link text-sm" data-testid={`footer-${link.name.toLowerCase().replace(/\s+/g, '-')}`}>
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Get In Touch */}
          <div>
            <h3 className="font-outfit font-semibold text-white mb-6">Get In Touch</h3>
            <p className="text-neutral-400 text-sm mb-6">
              Ready to discuss your waste management needs? Contact us today for a free quote.
            </p>
            <Link to="/contact" className="btn-primary inline-block text-sm" data-testid="footer-cta-button">
              Contact Us
            </Link>

            {partners.length > 0 && (
              <div className="mt-8" data-testid="footer-partners">
                <p className="text-neutral-500 text-[11px] uppercase tracking-wider mb-3">Partners &amp; Accreditations</p>
                <div className="grid grid-cols-4 gap-2">
                  {partners.slice(0, 8).map(p => {
                    const src = p.image_url?.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${p.image_url}` : p.image_url;
                    const img = (
                      <img
                        src={src}
                        alt={p.name || 'Partner logo'}
                        className="max-h-9 max-w-full object-contain opacity-90 group-hover:opacity-100 transition-opacity"
                        loading="lazy"
                      />
                    );
                    return (
                      <div
                        key={p.id}
                        className="bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg h-14 flex items-center justify-center px-1.5 group transition-colors"
                        data-testid={`footer-partner-${p.order}`}
                      >
                        {p.link_url ? (
                          <a href={p.link_url} target="_blank" rel="noopener noreferrer" className="block w-full h-full flex items-center justify-center">
                            {img}
                          </a>
                        ) : img}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer Document Links */}
        {footerDocs.length > 0 && (
          <div className="mt-12 pt-8 border-t border-neutral-800">
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
              {footerDocs.map((doc) => (
                doc.pdf_url ? (
                  <a key={doc.id} href={doc.pdf_url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${doc.pdf_url}` : doc.pdf_url} target="_blank" rel="noopener noreferrer" className="text-neutral-500 hover:text-neutral-300 text-xs transition-colors" data-testid={`footer-doc-${doc.slug}`}>
                    {doc.label}
                  </a>
                ) : (
                  <span key={doc.id} className="text-neutral-600 text-xs cursor-default" data-testid={`footer-doc-${doc.slug}`}>
                    {doc.label}
                  </span>
                )
              ))}
            </div>
          </div>
        )}

        {/* Bottom Bar */}
        <div className="mt-8 pt-8 border-t border-neutral-800 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-neutral-500 text-sm">
            &copy; {new Date().getFullYear()} North Somerset Environment Company Ltd. All rights reserved.
          </p>
          <Link to="/login" className="text-neutral-500 hover:text-lime-400 text-sm transition-colors flex items-center gap-1.5" data-testid="footer-employee-login">
            <SignIn size={16} />Employee Login
          </Link>
        </div>
      </div>
    </footer>
  );
};
